import { useState, useEffect, useRef } from "react";
import { GripVertical, X, Check, Pencil } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * SortableRow — canonical drag-and-drop row for reorderable, deletable lists.
 *
 * Layout:  [grip 24px]  [seq# 40px]  [label 1fr]  [usage-chip? rename? remove?]
 *
 * Behaviors baked in:
 *   - Drag handle (GripVertical icon) is the ONLY drag surface, so the whole
 *     row stays clickable without triggering a drag by accident.
 *   - Delete is a two-step inline confirmation ("Sil? ✓ ×") — no popup.
 *   - Rename (optional, opt-in via `onRename`): pencil icon flips the label
 *     into an inline input. Enter commits (calls `onRename(oldName, newName)`)
 *     — the CALLER decides whether to actually apply the rename or open a
 *     propagation confirmation modal. Esc cancels.
 *   - Usage chip (optional, opt-in via `usageCount > 0`): a persistent
 *     `● N tabloda` chip next to the label. Serves both as a visual indicator
 *     of downstream usage AND as a retroactive "adları eşitle" entry point —
 *     click → same rename flow as the pencil, but always visible so merchants
 *     don't need to hover to discover it. Tooltip explains what happens.
 *
 * Props:
 *   - `id`            unique sortable id (usually the item's canonical string)
 *   - `index`         zero-based position (used for the 01, 02, … prefix)
 *   - `label`         primary text (must be a string when `onRename` is used)
 *   - `onRemove`      called on delete confirmation
 *   - `onRename`      optional — signature `(oldName, newName) => void`
 *   - `usageCount`    optional — when > 0 renders the persistent usage chip
 *   - `testIdPrefix`  e.g. "measure" → renders `data-testid="measure-drag-0"`,
 *                     `measure-remove-0`, `measure-remove-confirm-0`.
 *   - `labelClassName` optional tailwind override for label typography
 */
export function SortableRow({
    id,
    index,
    label,
    onRemove,
    onRename,
    usageCount = 0,
    testIdPrefix,
    labelClassName = "font-display font-semibold",
}) {
    const [confirming, setConfirming] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState("");
    const inputRef = useRef(null);
    useEffect(() => { if (renaming) inputRef.current?.focus(); }, [renaming]);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 10 : "auto",
    };
    // Cancel any pending delete-confirmation when the user starts dragging —
    // otherwise the prompt would linger on the wrong row after drop.
    if (isDragging && confirming) setConfirming(false);

    const commitRename = () => {
        const nextName = draft.trim();
        if (!nextName || nextName === label) { setRenaming(false); return; }
        onRename && onRename(label, nextName);
        setRenaming(false);
    };
    const startRename = () => { setDraft(String(label)); setRenaming(true); };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group grid items-center border-b border-border last:border-0 hover:bg-muted/30 text-sm bg-surface"
            data-testid={`${testIdPrefix}-row-${index}`}
            {...attributes}
        >
            <div className="grid items-center" style={{ gridTemplateColumns: "24px 40px 1fr min-content auto" }}>
                <button
                    type="button"
                    {...listeners}
                    className="h-full flex items-center justify-center text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                    aria-label="Sürükleyerek sırala"
                    title="Sürükleyerek sırala"
                    data-testid={`${testIdPrefix}-drag-${index}`}
                >
                    <GripVertical className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
                <div className="px-1 py-2.5 text-[10px] font-mono text-muted-foreground">
                    {String(index + 1).padStart(2, "0")}
                </div>
                <div className={`px-2 py-2.5 min-w-0 flex items-center ${labelClassName}`}>
                    {renaming ? (
                        <input
                            ref={inputRef}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                                if (e.key === "Escape") { e.preventDefault(); setRenaming(false); }
                            }}
                            onBlur={commitRename}
                            className="w-full bg-background border border-foreground/30 rounded-md px-2 py-0.5 text-sm outline-none"
                            data-testid={`${testIdPrefix}-rename-input-${index}`}
                        />
                    ) : (
                        <span className="truncate">{label}</span>
                    )}
                </div>
                {/* Usage chip — always in its own grid column so labels across
                    rows stay aligned. Empty div preserves the slot when the
                    row has no downstream usage. */}
                <div className="pr-2">
                    {usageCount > 0 && onRename && !renaming && (
                        <button
                            type="button"
                            onClick={startRename}
                            className="whitespace-nowrap inline-flex items-center gap-1 rounded-md border border-border bg-muted/60 hover:bg-muted hover:border-foreground/30 transition-colors px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground"
                            title={`Bu öğe ${usageCount} tabloda kullanılıyor · adları eşitlemek için tıkla`}
                            aria-label={`${usageCount} tabloda kullanılıyor — adları eşitle`}
                            data-testid={`${testIdPrefix}-usage-${index}`}
                        >
                            <span className="h-1 w-1 rounded-full bg-brand" aria-hidden="true" />
                            {usageCount} tabloda
                        </button>
                    )}
                </div>
                {confirming ? (
                    <div
                        className="flex items-center gap-1 pr-2 pl-1"
                        data-testid={`${testIdPrefix}-remove-confirm-${index}`}
                    >
                        <span className="text-[10.5px] text-muted-foreground select-none">Sil?</span>
                        <button
                            type="button"
                            onClick={() => { onRemove(); setConfirming(false); }}
                            className="h-6 w-6 rounded-md grid place-items-center text-destructive hover:bg-destructive/10 transition-colors"
                            aria-label="Silmeyi onayla"
                            title="Sil"
                            data-testid={`${testIdPrefix}-remove-confirm-yes-${index}`}
                        >
                            <Check className="h-3.5 w-3.5" strokeWidth={2.25} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirming(false)}
                            className="h-6 w-6 rounded-md grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            aria-label="Vazgeç"
                            title="Vazgeç"
                            data-testid={`${testIdPrefix}-remove-confirm-no-${index}`}
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center pr-2">
                        {onRename && !renaming && (
                            <button
                                type="button"
                                onClick={startRename}
                                className="px-2 text-muted-foreground/60 opacity-0 group-hover:opacity-100 hover:text-foreground transition-all"
                                data-testid={`${testIdPrefix}-rename-${index}`}
                                aria-label="Bu satırı yeniden adlandır"
                                title="Yeniden adlandır"
                            >
                                <Pencil className="h-3 w-3" />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setConfirming(true)}
                            className="px-2 text-muted-foreground/60 opacity-0 group-hover:opacity-100 hover:text-destructive transition-all"
                            data-testid={`${testIdPrefix}-remove-${index}`}
                            aria-label="Bu satırı kaldır"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
